
import { useState, useEffect, useRef } from 'react';

interface DataPoint {
  year: number;
  fixedRate: number;
  variableRate: number;
}

const mortgageData: DataPoint[] = [
  { year: 1999, fixedRate: 7.39, variableRate: 7.29 },
  { year: 2000, fixedRate: 8.20, variableRate: 7.27 },
  { year: 2001, fixedRate: 7.18, variableRate: 5.81 },
  { year: 2002, fixedRate: 6.70, variableRate: 4.20 },
  { year: 2003, fixedRate: 6.04, variableRate: 4.69 },
  { year: 2004, fixedRate: 5.80, variableRate: 4.00 },
  { year: 2005, fixedRate: 5.48, variableRate: 4.35 },
  { year: 2006, fixedRate: 5.98, variableRate: 5.81 },
  { year: 2007, fixedRate: 6.36, variableRate: 6.10 },
  { year: 2008, fixedRate: 6.41, variableRate: 4.73 },
  { year: 2009, fixedRate: 5.05, variableRate: 2.40 },
  { year: 2010, fixedRate: 4.82, variableRate: 2.60 },
  { year: 2011, fixedRate: 4.57, variableRate: 3.00 },
  { year: 2012, fixedRate: 4.24, variableRate: 3.00 },
  { year: 2013, fixedRate: 4.17, variableRate: 3.00 },
  { year: 2014, fixedRate: 4.08, variableRate: 3.00 },
  { year: 2015, fixedRate: 3.77, variableRate: 2.78 },
  { year: 2016, fixedRate: 3.70, variableRate: 2.70 },
  { year: 2017, fixedRate: 3.79, variableRate: 2.87 },
  { year: 2018, fixedRate: 4.36, variableRate: 3.60 },
  { year: 2019, fixedRate: 4.25, variableRate: 3.95 },
  { year: 2020, fixedRate: 3.72, variableRate: 2.78 },
  { year: 2021, fixedRate: 3.28, variableRate: 2.45 },
  { year: 2022, fixedRate: 4.91, variableRate: 4.20 },
  { year: 2023, fixedRate: 6.04, variableRate: 6.97 },
  { year: 2024, fixedRate: 5.88, variableRate: 6.68 }
];

export default function MortgageRatesChart() {
  const [hoveredPoint, setHoveredPoint] = useState<{ x: number; y: number; data: DataPoint; type: 'fixed' | 'variable' } | null>(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 400 });
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const isMobile = window.innerWidth < 768;
        setDimensions({
          width: Math.max(containerWidth - 40, 300),
          height: isMobile ? 300 : 400
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  const padding = { top: 40, right: 40, bottom: 60, left: 60 };
  const chartWidth = dimensions.width - padding.left - padding.right;
  const chartHeight = dimensions.height - padding.top - padding.bottom;

  const minYear = Math.min(...mortgageData.map(d => d.year));
  const maxYear = Math.max(...mortgageData.map(d => d.year));
  const minRate = Math.min(...mortgageData.flatMap(d => [d.fixedRate, d.variableRate]));
  const maxRate = Math.max(...mortgageData.flatMap(d => [d.fixedRate, d.variableRate]));

  const xScale = (year: number) => ((year - minYear) / (maxYear - minYear)) * chartWidth;
  const yScale = (rate: number) => chartHeight - ((rate - minRate) / (maxRate - minRate)) * chartHeight;

  const createPath = (data: DataPoint[], rateType: 'fixedRate' | 'variableRate') => {
    return data.map((d, i) => {
      const x = xScale(d.year);
      const y = yScale(d[rateType]);
      return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
    }).join(' ');
  };

  const fixedPath = createPath(mortgageData, 'fixedRate');
  const variablePath = createPath(mortgageData, 'variableRate');

  const handleMouseMove = (event: React.MouseEvent<SVGSVGElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left - padding.left;
    const y = event.clientY - rect.top - padding.top;

    if (x < 0 || x > chartWidth || y < 0 || y > chartHeight) {
      setHoveredPoint(null);
      return;
    }

    const yearFromX = minYear + (x / chartWidth) * (maxYear - minYear);
    const closestDataPoint = mortgageData.reduce((prev, curr) => 
      Math.abs(curr.year - yearFromX) < Math.abs(prev.year - yearFromX) ? curr : prev
    );

    const fixedY = yScale(closestDataPoint.fixedRate);
    const variableY = yScale(closestDataPoint.variableRate);
    const fixedDistance = Math.abs(y - fixedY);
    const variableDistance = Math.abs(y - variableY);

    if (fixedDistance < variableDistance && fixedDistance < 20) {
      setHoveredPoint({
        x: xScale(closestDataPoint.year),
        y: fixedY,
        data: closestDataPoint,
        type: 'fixed'
      });
    } else if (variableDistance < 20) {
      setHoveredPoint({
        x: xScale(closestDataPoint.year),
        y: variableY,
        data: closestDataPoint,
        type: 'variable'
      });
    } else {
      setHoveredPoint(null);
    }
  };

  const generateYAxisTicks = () => {
    const tickCount = 6;
    const ticks = [];
    for (let i = 0; i <= tickCount; i++) {
      const rate = minRate + (i / tickCount) * (maxRate - minRate);
      ticks.push(Math.round(rate * 10) / 10);
    }
    return ticks;
  };

  const generateXAxisTicks = () => {
    const ticks = [];
    for (let year = minYear; year <= maxYear; year += 5) {
      ticks.push(year);
    }
    return ticks;
  };

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-white">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            25-Year Comparison: <span className="text-red-600">Fixed vs Variable Mortgage Rates</span>
          </h2>
          <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
            Historical perspective on mortgage rate trends to help inform your financing decisions
          </p>
        </div>

        <div ref={containerRef} className="bg-white rounded-2xl shadow-lg border border-gray-200 p-4 sm:p-6 lg:p-8">
          <div className="relative">
            <svg
              width={dimensions.width}
              height={dimensions.height}
              className="w-full h-auto"
              onMouseMove={handleMouseMove}
              onMouseLeave={() => setHoveredPoint(null)}
            >
              {/* Grid lines */}
              <defs>
                <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                  <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#f3f4f6" strokeWidth="1"/>
                </pattern>
              </defs>
              <rect width={chartWidth} height={chartHeight} x={padding.left} y={padding.top} fill="url(#grid)" />

              {/* Y-axis */}
              <line
                x1={padding.left}
                y1={padding.top}
                x2={padding.left}
                y2={padding.top + chartHeight}
                stroke="#374151"
                strokeWidth="2"
              />

              {/* X-axis */}
              <line
                x1={padding.left}
                y1={padding.top + chartHeight}
                x2={padding.left + chartWidth}
                y2={padding.top + chartHeight}
                stroke="#374151"
                strokeWidth="2"
              />

              {/* Y-axis ticks and labels */}
              {generateYAxisTicks().map((rate, i) => (
                <g key={i}>
                  <line
                    x1={padding.left - 5}
                    y1={padding.top + yScale(rate)}
                    x2={padding.left}
                    y2={padding.top + yScale(rate)}
                    stroke="#374151"
                    strokeWidth="1"
                  />
                  <text
                    x={padding.left - 10}
                    y={padding.top + yScale(rate) + 4}
                    textAnchor="end"
                    className="text-xs sm:text-sm fill-gray-600"
                  >
                    {rate.toFixed(1)}%
                  </text>
                </g>
              ))}

              {/* X-axis ticks and labels */}
              {generateXAxisTicks().map((year, i) => (
                <g key={i}>
                  <line
                    x1={padding.left + xScale(year)}
                    y1={padding.top + chartHeight}
                    x2={padding.left + xScale(year)}
                    y2={padding.top + chartHeight + 5}
                    stroke="#374151"
                    strokeWidth="1"
                  />
                  <text
                    x={padding.left + xScale(year)}
                    y={padding.top + chartHeight + 20}
                    textAnchor="middle"
                    className="text-xs sm:text-sm fill-gray-600"
                  >
                    {year}
                  </text>
                </g>
              ))}

              {/* Fixed rate line */}
              <path
                d={fixedPath}
                fill="none"
                stroke="#C8102E"
                strokeWidth="3"
                transform={`translate(${padding.left}, ${padding.top})`}
              />

              {/* Variable rate line */}
              <path
                d={variablePath}
                fill="none"
                stroke="#000000"
                strokeWidth="3"
                transform={`translate(${padding.left}, ${padding.top})`}
              />

              {/* Data points */}
              {mortgageData.map((d, i) => (
                <g key={i}>
                  <circle
                    cx={padding.left + xScale(d.year)}
                    cy={padding.top + yScale(d.fixedRate)}
                    r="4"
                    fill="#C8102E"
                    className="hover:r-6 transition-all duration-200 cursor-pointer"
                  />
                  <circle
                    cx={padding.left + xScale(d.year)}
                    cy={padding.top + yScale(d.variableRate)}
                    r="4"
                    fill="#000000"
                    className="hover:r-6 transition-all duration-200 cursor-pointer"
                  />
                </g>
              ))}

              {/* Hover tooltip */}
              {hoveredPoint && (
                <g>
                  <circle
                    cx={padding.left + hoveredPoint.x}
                    cy={padding.top + hoveredPoint.y}
                    r="6"
                    fill={hoveredPoint.type === 'fixed' ? '#C8102E' : '#000000'}
                    stroke="white"
                    strokeWidth="2"
                  />
                  <rect
                    x={padding.left + hoveredPoint.x - 60}
                    y={padding.top + hoveredPoint.y - 40}
                    width="120"
                    height="30"
                    fill="rgba(0, 0, 0, 0.8)"
                    rx="4"
                  />
                  <text
                    x={padding.left + hoveredPoint.x}
                    y={padding.top + hoveredPoint.y - 25}
                    textAnchor="middle"
                    className="text-xs fill-white font-semibold"
                  >
                    {hoveredPoint.data.year}
                  </text>
                  <text
                    x={padding.left + hoveredPoint.x}
                    y={padding.top + hoveredPoint.y - 12}
                    textAnchor="middle"
                    className="text-xs fill-white"
                  >
                    {hoveredPoint.type === 'fixed' ? 'Fixed' : 'Variable'}: {hoveredPoint.type === 'fixed' ? hoveredPoint.data.fixedRate : hoveredPoint.data.variableRate}%
                  </text>
                </g>
              )}
            </svg>

            {/* Legend */}
            <div className="flex flex-wrap justify-center gap-4 sm:gap-6 mt-6">
              <div className="flex items-center gap-2">
                <div className="w-4 h-0.5 bg-red-600"></div>
                <span className="text-sm sm:text-base text-gray-700 font-medium">Fixed Rate</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-0.5 bg-black"></div>
                <span className="text-sm sm:text-base text-gray-700 font-medium">Variable Rate</span>
              </div>
            </div>
          </div>
        </div>

        {/* Axis labels */}
        <div className="text-center mt-4">
          <div className="text-sm text-gray-600 mb-2">Interest Rate (%)</div>
          <div className="text-sm text-gray-600">Years (1999-2024)</div>
        </div>
      </div>
    </section>
  );
}
